import type { ResidenceFieldKey, ResidenceVerifiedFields } from "./callScriptResidence.types";
import { isValidIsoDate, isValidPhoneNumber } from "./callScriptValidation";

// ---------------------------------------------------------------------------
// callScriptResidenceValidation
//
// Save-time validation for the residence Call Script (CAPS2 validateCusVer),
// with three legacy defects fixed:
//   - Phone Number Dialed is required only while its row is visible, so an
//     expired question can't block Save forever;
//   - errors are recomputed from scratch each time, so a cleared field never
//     keeps a stale error;
//   - the duplicate-phone check compares effective (verified, else saved)
//     numbers, so Primary matching the saved Secondary is always caught.
// ---------------------------------------------------------------------------

/** Field-format failures carry the field's own key; both phones share `duplicatePhone`. */
export type ResidenceValidationError =
  | "phoneNumberDialed"
  | "dateOfBirth"
  | "primaryNumber"
  | "secondaryNumber"
  | "physicalZip"
  | "mailingZip"
  | "landlordPhone"
  | "landlordZip"
  | "duplicatePhone";

export type ResidenceFieldErrors = Partial<Record<ResidenceFieldKey, ResidenceValidationError>>;

const PHONE_FIELDS = ["primaryNumber", "secondaryNumber"] as const;
const ZIP_FIELDS = ["physicalZip", "mailingZip"] as const;

/**
 * Strips formatting from a phone or ZIP value.
 * @param {string} value - The raw value.
 * @returns {string} Digits only.
 */
function digitsOf(value: string): string {
  return value.replaceAll(/\D/gu, "");
}

/**
 * Validates the "Verified Information" column before the save confirmation opens.
 * @param {ResidenceVerifiedFields} verified - The analyst-entered values.
 * @param {ResidenceVerifiedFields} saved - The "Saved Information" values.
 * @param {(key: ResidenceFieldKey) => boolean} isVisible - Whether a field's row is rendered.
 * @returns {ResidenceFieldErrors} One error per invalid field; empty when valid.
 */
export function validateResidenceCallScript(
  verified: ResidenceVerifiedFields,
  saved: ResidenceVerifiedFields,
  isVisible: (key: ResidenceFieldKey) => boolean
): ResidenceFieldErrors {
  const errors: ResidenceFieldErrors = {};

  if (isVisible("phoneNumberDialed") && !isValidPhoneNumber(digitsOf(verified.phoneNumberDialed))) {
    errors.phoneNumberDialed = "phoneNumberDialed";
  }

  const dateOfBirth = verified.dateOfBirth.trim();
  if (dateOfBirth && !isValidIsoDate(dateOfBirth)) {
    errors.dateOfBirth = "dateOfBirth";
  }

  for (const field of PHONE_FIELDS) {
    const entered = digitsOf(verified[field]);
    if (entered && !isValidPhoneNumber(entered)) {
      errors[field] = field;
    }
  }

  const primary = digitsOf(verified.primaryNumber) || digitsOf(saved.primaryNumber);
  const secondary = digitsOf(verified.secondaryNumber) || digitsOf(saved.secondaryNumber);
  if (primary && primary === secondary) {
    for (const field of PHONE_FIELDS) {
      if (digitsOf(verified[field]) && !errors[field]) {
        errors[field] = "duplicatePhone";
      }
    }
  }

  for (const field of ZIP_FIELDS) {
    const zip = verified[field].trim();
    if (zip && !/^\d{5}$/u.test(zip)) {
      errors[field] = field;
    }
  }

  // Landlord rows hide for Self/Spouse; a hidden row's leftover value must not block Save.
  const landlordPhone = digitsOf(verified.landlordPhone);
  if (isVisible("landlordPhone") && landlordPhone && !isValidPhoneNumber(landlordPhone)) {
    errors.landlordPhone = "landlordPhone";
  }
  const landlordZip = verified.landlordZip.trim();
  if (isVisible("landlordZip") && landlordZip && !/^\d{5}$/u.test(landlordZip)) {
    errors.landlordZip = "landlordZip";
  }

  return errors;
}
